// KV-backed mock for Images binding CRUD operations
// Image data is stored as KV values, metadata as KV metadata
// Transforms and info operations are handled via HTTP loopback to Node.js Sharp

import { RpcTarget, WorkerEntrypoint } from "cloudflare:workers";
import { getPublicUrl } from "miniflare:shared";
import { CoreBindings, CoreHeaders, CorePaths } from "../core/constants";

interface Env {
	IMAGES_STORE: KVNamespace;
	[CoreBindings.SERVICE_LOOPBACK]: Fetcher;
}

function buildVariantUrl(
	publicUrl: URL,
	imageId: string,
	variant: string
): string {
	return new URL(
		`${CorePaths.IMAGE_DELIVERY}/${imageId}/${variant}`,
		publicUrl
	).toString();
}

function buildUploadUrl(publicUrl: URL, imageId: string): string {
	return new URL(`${CorePaths.IMAGE_UPLOAD}/${imageId}`, publicUrl).toString();
}

function draftExpiryKey(imageId: string): string {
	return `${imageId}:direct-upload-expiry`;
}

// Rewrites stored variant names (e.g. `["public"]`) to absolute URLs.
async function withResolvedVariants(
	metadata: ImageMetadata,
	env: Env
): Promise<ImageMetadata> {
	const publicUrl = await getPublicUrl(env[CoreBindings.SERVICE_LOOPBACK]);
	return {
		...metadata,
		variants: metadata.variants.map((variant) =>
			buildVariantUrl(publicUrl, metadata.id, variant)
		),
	};
}

function base64DecodeArrayBuffer(buffer: ArrayBuffer): ArrayBuffer {
	const decoder = new TextDecoder();
	const base64String = decoder.decode(buffer);
	const binaryString = atob(base64String.trim());
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes.buffer;
}

async function base64DecodeStream(
	stream: ReadableStream<Uint8Array>
): Promise<ArrayBuffer> {
	const response = new Response(stream);
	const buffer = await response.arrayBuffer();
	return base64DecodeArrayBuffer(buffer);
}

function resolveMetaPath(obj: unknown, path: string): unknown {
	return path
		.split(".")
		.reduce<unknown>(
			(acc, key) =>
				acc && typeof acc === "object"
					? (acc as Record<string, unknown>)[key]
					: undefined,
			obj
		);
}

function matchesCondition(
	actual: unknown,
	condition: ImageMetadataFilterValue
): boolean {
	if (
		condition === null ||
		typeof condition !== "object" ||
		Array.isArray(condition)
	) {
		return actual === condition;
	}

	return Object.entries(condition).every(([op, expected]) => {
		switch (op) {
			case "eq":
				return actual === expected;
			case "in":
				return (
					Array.isArray(expected) &&
					expected.some((candidate) => candidate === actual)
				);
			case "gt":
				return typeof actual === "number" && actual > (expected as number);
			case "gte":
				return typeof actual === "number" && actual >= (expected as number);
			case "lt":
				return typeof actual === "number" && actual < (expected as number);
			case "lte":
				return typeof actual === "number" && actual <= (expected as number);
			default:
				return false;
		}
	});
}

// AND logic across fields, matching images-core/images-edge-api behaviour.
function matchesMetadataFilters(
	image: ImageMetadata,
	filters: Record<string, ImageMetadataFilterValue> | undefined
): boolean {
	if (!filters) {
		return true;
	}

	return Object.entries(filters).every(([field, condition]) =>
		matchesCondition(resolveMetaPath(image.meta ?? {}, field), condition)
	);
}

// No real account signing key store exists in local dev, so this single
// secret stands in for every account and `keyName`; it has no security value.
const LOCAL_SIGNING_SECRET = "miniflare-local-dev-images-signing-key";

async function hmacSha256Hex(secret: string, value: string): Promise<string> {
	const encoder = new TextEncoder();
	const key = await crypto.subtle.importKey(
		"raw",
		encoder.encode(secret),
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"]
	);
	const signature = await crypto.subtle.sign(
		"HMAC",
		key,
		encoder.encode(value)
	);
	return Array.from(new Uint8Array(signature))
		.map((byte) => byte.toString(16).padStart(2, "0"))
		.join("");
}

function assertVariantName(variant: string): void {
	if (variant === "") {
		throw new Error("variant is required");
	}
	if (/[/?#%]/.test(variant)) {
		throw new Error("variant contains invalid URL path characters");
	}
}

function resolveExpiresAt(expiresIn: number | undefined): number | undefined {
	if (expiresIn === undefined) {
		return undefined;
	}
	if (!Number.isInteger(expiresIn) || expiresIn <= 0) {
		throw new Error("expiresIn must be a positive integer");
	}
	return Math.floor(Date.now() / 1000) + expiresIn;
}

// Returns `null` when valid, or an error message otherwise.
async function verifySignedRequest(url: URL): Promise<string | null> {
	const sig = url.searchParams.get("sig");
	if (!sig) {
		return "Missing signature";
	}

	const exp = url.searchParams.get("exp");
	if (exp !== null) {
		const expiresAt = Number.parseInt(exp, 10);
		if (Number.isNaN(expiresAt) || expiresAt < Date.now() / 1000) {
			return "Signature expired";
		}
	}

	const unsignedUrl = new URL(url);
	unsignedUrl.searchParams.delete("sig");
	const expectedSig = await hmacSha256Hex(
		LOCAL_SIGNING_SECRET,
		`${unsignedUrl.pathname}${unsignedUrl.search}`
	);
	if (sig !== expectedSig) {
		return "Invalid signature";
	}

	return null;
}

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MIN_DIRECT_UPLOAD_EXPIRES_IN = 120;
const MAX_DIRECT_UPLOAD_EXPIRES_IN = 21600;
const DEFAULT_DIRECT_UPLOAD_EXPIRES_IN = 1800;

function assertCustomIdNotUuid(id: string): void {
	if (UUID_PATTERN.test(id)) {
		throw new Error("CustomID must not be UUID");
	}
}

function resolveDirectUploadExpiresAt(expiresIn: number | undefined): number {
	const duration = expiresIn ?? DEFAULT_DIRECT_UPLOAD_EXPIRES_IN;
	if (
		duration <= MIN_DIRECT_UPLOAD_EXPIRES_IN ||
		duration >= MAX_DIRECT_UPLOAD_EXPIRES_IN
	) {
		throw new Error("expiry is out of accepted bound.");
	}
	return Math.floor(Date.now() / 1000) + duration;
}

class ImageHandleImpl extends RpcTarget {
	readonly #imageId: string;
	readonly #env: Env;

	constructor(imageId: string, env: Env) {
		super();
		this.#imageId = imageId;
		this.#env = env;
	}

	async details(): Promise<ImageMetadata | null> {
		const result = await this.#env.IMAGES_STORE.getWithMetadata<ImageMetadata>(
			this.#imageId,
			"arrayBuffer"
		);
		if (result.metadata === null) {
			return null;
		}
		return withResolvedVariants(result.metadata, this.#env);
	}

	async bytes(): Promise<ReadableStream<Uint8Array> | null> {
		const data = await this.#env.IMAGES_STORE.get(this.#imageId, "arrayBuffer");
		if (data === null) {
			return null;
		}
		return new Blob([data]).stream();
	}

	async signedUrl(options: ImageSignedUrlOptions): Promise<string> {
		assertVariantName(options.variant);

		const publicUrl = await getPublicUrl(
			this.#env[CoreBindings.SERVICE_LOOPBACK]
		);
		const expiresAt = resolveExpiresAt(options.expiresIn);
		const url = new URL(
			buildVariantUrl(publicUrl, this.#imageId, options.variant)
		);
		if (expiresAt !== undefined) {
			url.searchParams.set("exp", String(expiresAt));
		}

		const signature = await hmacSha256Hex(
			LOCAL_SIGNING_SECRET,
			`${url.pathname}${url.search}`
		);
		url.searchParams.set("sig", signature);
		return url.toString();
	}

	async update(options: ImageUpdateOptions): Promise<ImageMetadata> {
		const existing =
			await this.#env.IMAGES_STORE.getWithMetadata<ImageMetadata>(
				this.#imageId,
				"arrayBuffer"
			);
		if (existing.value === null || existing.metadata === null) {
			throw new Error(`Image not found: ${this.#imageId}`);
		}

		const updatedMetadata: ImageMetadata = {
			...existing.metadata,
			requireSignedURLs:
				options.requireSignedURLs ?? existing.metadata.requireSignedURLs,
			meta: options.metadata ?? existing.metadata.meta,
			creator: options.creator ?? existing.metadata.creator,
		};

		await this.#env.IMAGES_STORE.put(this.#imageId, existing.value, {
			metadata: updatedMetadata,
		});
		return withResolvedVariants(updatedMetadata, this.#env);
	}

	async delete(): Promise<boolean> {
		const existing = await this.#env.IMAGES_STORE.get(
			this.#imageId,
			"arrayBuffer"
		);
		if (existing === null) {
			return false;
		}
		await this.#env.IMAGES_STORE.delete(this.#imageId);
		return true;
	}
}

export default class ImagesService extends WorkerEntrypoint<Env> {
	image(imageId: string): ImageHandleImpl {
		return new ImageHandleImpl(imageId, this.env);
	}

	async upload(
		image: ReadableStream<Uint8Array> | ArrayBuffer,
		options?: ImageUploadOptions
	): Promise<ImageMetadata> {
		let imageData: ReadableStream<Uint8Array> | ArrayBuffer = image;
		if (options?.encoding === "base64") {
			imageData =
				image instanceof ArrayBuffer
					? base64DecodeArrayBuffer(image)
					: await base64DecodeStream(image);
		}

		const buffer =
			imageData instanceof ArrayBuffer
				? imageData
				: await new Response(imageData).arrayBuffer();

		const id = options?.id ?? crypto.randomUUID();

		const metadata: ImageMetadata = {
			id,
			filename: options?.filename ?? "uploaded.jpg",
			uploaded: new Date().toISOString(),
			requireSignedURLs: options?.requireSignedURLs ?? false,
			meta: options?.metadata ?? {},
			variants: ["public"],
			draft: false,
			creator: options?.creator,
		};

		await this.env.IMAGES_STORE.put(id, buffer, { metadata });
		return withResolvedVariants(metadata, this.env);
	}

	async createDirectUpload(
		options?: ImageDirectUploadOptions
	): Promise<ImageDirectUploadResult> {
		if (options?.id !== undefined) {
			assertCustomIdNotUuid(options.id);
			if (options.requireSignedURLs) {
				throw new Error("Private custom ID is not supported");
			}
		}

		const expiresAt = resolveDirectUploadExpiresAt(options?.expiresIn);
		const id = options?.id ?? crypto.randomUUID();

		const metadata: ImageMetadata = {
			id,
			uploaded: new Date().toISOString(),
			requireSignedURLs: options?.requireSignedURLs ?? false,
			meta: options?.metadata ?? {},
			variants: ["public"],
			draft: true,
			creator: options?.creator,
		};

		await this.env.IMAGES_STORE.put(id, new ArrayBuffer(0), { metadata });
		await this.env.IMAGES_STORE.put(draftExpiryKey(id), String(expiresAt));

		const publicUrl = await getPublicUrl(
			this.env[CoreBindings.SERVICE_LOOPBACK]
		);
		return { id, uploadURL: buildUploadUrl(publicUrl, id) };
	}

	async list(options?: ImageListOptions): Promise<ImageList> {
		const limit = options?.limit ?? 50;

		// Fetch all keys so we can filter and sort accurately
		const allImages: ImageMetadata[] = [];
		let kvCursor: string | undefined;
		do {
			const kvResult = await this.env.IMAGES_STORE.list<ImageMetadata>({
				cursor: kvCursor,
			});
			for (const key of kvResult.keys) {
				if (key.metadata) {
					allImages.push(key.metadata);
				}
			}
			kvCursor = kvResult.list_complete ? undefined : kvResult.cursor;
		} while (kvCursor);

		if (options?.creator) {
			allImages.splice(
				0,
				allImages.length,
				...allImages.filter((i) => i.creator === options.creator)
			);
		}

		if (options?.filter?.metadata) {
			const metadataFilter = options.filter.metadata;
			allImages.splice(
				0,
				allImages.length,
				...allImages.filter((i) => matchesMetadataFilters(i, metadataFilter))
			);
		}

		allImages.sort((a, b) => {
			const dateA = a.uploaded ?? "";
			const dateB = b.uploaded ?? "";
			const cmp = dateA.localeCompare(dateB) || a.id.localeCompare(b.id);
			return options?.sortOrder === "desc" ? -cmp : cmp;
		});

		// Handle cursor-based pagination over the sorted/filtered results
		let startIndex = 0;
		if (options?.cursor) {
			const cursorIndex = allImages.findIndex((i) => i.id === options.cursor);
			if (cursorIndex >= 0) {
				startIndex = cursorIndex + 1;
			}
		}

		const page = allImages.slice(startIndex, startIndex + limit);
		const hasMore = startIndex + limit < allImages.length;
		const lastImage = page[page.length - 1];

		const publicUrl = await getPublicUrl(
			this.env[CoreBindings.SERVICE_LOOPBACK]
		);
		const resolvedPage = page.map((metadata) => ({
			...metadata,
			variants: metadata.variants.map((variant) =>
				buildVariantUrl(publicUrl, metadata.id, variant)
			),
		}));

		return {
			images: resolvedPage,
			cursor: hasMore && lastImage ? lastImage.id : undefined,
			listComplete: !hasMore,
		};
	}

	async #detectContentType(data: ArrayBuffer): Promise<string> {
		const formData = new FormData();
		formData.append("image", new Blob([data]));

		const infoRequest = new Request("http://placeholder/info", {
			method: "POST",
			body: formData,
		});
		infoRequest.headers.set(
			CoreHeaders.CUSTOM_FETCH_SERVICE,
			CoreBindings.IMAGES_BINDING_SERVICE
		);

		const response =
			await this.env[CoreBindings.SERVICE_LOOPBACK].fetch(infoRequest);
		if (response.ok) {
			const info = (await response.json()) as { format?: string };
			if (info.format) {
				return info.format;
			}
		}
		return "application/octet-stream";
	}

	async #completeDirectUpload(request: Request, url: URL): Promise<Response> {
		if (request.method !== "POST") {
			return new Response("Method not allowed", { status: 405 });
		}

		const imageId = url.pathname.slice(CorePaths.IMAGE_UPLOAD.length + 1);
		if (!imageId) {
			return new Response("Missing image ID", { status: 400 });
		}

		const existing = await this.env.IMAGES_STORE.getWithMetadata<ImageMetadata>(
			imageId,
			"arrayBuffer"
		);
		if (existing.metadata === null) {
			return new Response("Upload link not found", { status: 404 });
		}
		if (!existing.metadata.draft) {
			return new Response("Upload link already used", { status: 409 });
		}

		const expiresAt = await this.env.IMAGES_STORE.get(draftExpiryKey(imageId));
		if (expiresAt === null || Number(expiresAt) < Date.now() / 1000) {
			return new Response("Upload link expired", { status: 410 });
		}

		const formData = await request.formData();
		const file = formData.get("file");
		if (!(file instanceof Blob)) {
			return new Response("Missing file", { status: 400 });
		}
		const buffer = await file.arrayBuffer();

		const completedMetadata: ImageMetadata = {
			...existing.metadata,
			filename: file instanceof File ? file.name : existing.metadata.filename,
			draft: false,
		};

		await this.env.IMAGES_STORE.put(imageId, buffer, {
			metadata: completedMetadata,
		});
		await this.env.IMAGES_STORE.delete(draftExpiryKey(imageId));

		return Response.json({ id: imageId, success: true });
	}

	// Handle HTTP requests for image delivery and transform operations
	async fetch(request: Request): Promise<Response> {
		const url = new URL(request.url);

		// Serve image bytes at /__cf_local/imagedelivery/<id>/<variant>
		if (url.pathname.startsWith(`${CorePaths.IMAGE_DELIVERY}/`)) {
			const parts = url.pathname
				.slice(CorePaths.IMAGE_DELIVERY.length + 1)
				.split("/");
			const imageId = parts[0];
			if (!imageId) {
				return new Response("Missing image ID", { status: 400 });
			}

			const { value: data, metadata } =
				await this.env.IMAGES_STORE.getWithMetadata<ImageMetadata>(
					imageId,
					"arrayBuffer"
				);
			if (data === null || metadata === null) {
				return new Response("Image not found", { status: 404 });
			}

			if (metadata.requireSignedURLs) {
				const verifyError = await verifySignedRequest(url);
				if (verifyError !== null) {
					return new Response(verifyError, { status: 401 });
				}
			}

			const contentType = await this.#detectContentType(data);
			return new Response(data, {
				headers: { "Content-Type": contentType },
			});
		}

		if (url.pathname.startsWith(`${CorePaths.IMAGE_UPLOAD}/`)) {
			return this.#completeDirectUpload(request, url);
		}

		// Forward transform/info operations to Node.js via loopback where Sharp runs
		const forwardRequest = new Request(request);
		forwardRequest.headers.set(
			CoreHeaders.CUSTOM_FETCH_SERVICE,
			CoreBindings.IMAGES_BINDING_SERVICE
		);
		forwardRequest.headers.set(CoreHeaders.ORIGINAL_URL, request.url);
		return this.env[CoreBindings.SERVICE_LOOPBACK].fetch(forwardRequest);
	}
}
